import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    if (!token) return toast.error("Missing reset token");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center"><ShieldCheck size={18} className="text-white"/></div>
          <span className="font-semibold text-slate-900 text-lg">Informed <span className="text-purple-600">Accelerator</span></span>
        </Link>
        <Card className="glass rounded-2xl">
          <CardContent className="p-8">
            <div className="eyebrow">Set new password</div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">Choose a new password</h1>
            {done ? (
              <div className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-400/30 p-5 text-slate-800" data-testid="reset-done-panel">
                <div className="flex items-center gap-2 text-slate-900 font-semibold"><CheckCircle2 size={16} className="text-emerald-300"/> Password updated</div>
                <p className="text-sm text-slate-700 mt-2">Redirecting you to sign in…</p>
              </div>
            ) : (
              <form className="mt-6 space-y-4" onSubmit={submit}>
                <div>
                  <Label className="text-slate-700">New password</Label>
                  <Input type="password" required minLength={8} value={pw} onChange={(e)=>setPw(e.target.value)} data-testid="reset-password-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                  <p className="text-xs text-slate-400 mt-1">At least 8 characters.</p>
                </div>
                <div>
                  <Label className="text-slate-700">Confirm password</Label>
                  <Input type="password" required minLength={8} value={pw2} onChange={(e)=>setPw2(e.target.value)} data-testid="reset-password-confirm-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <Button type="submit" disabled={loading || !token} data-testid="reset-submit-button" className="btn-primary rounded-xl w-full py-6">
                  {loading ? "Saving…" : "Update password"}
                </Button>
                {!token && <p className="text-xs text-red-300 text-center">Missing token. Please use the link from your email.</p>}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
